import { ReactNode, useEffect, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { getDashboardOverview, DashboardOverview } from '../services/api'
import { useAuth } from '../context/AuthContext'

interface LayoutProps {
  children: ReactNode
}

const Layout = ({ children }: LayoutProps) => {
  const location = useLocation()
  const navigate = useNavigate()
  const { user, logout } = useAuth()
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)
  const [stats, setStats] = useState<DashboardOverview | null>(null)
  const [statsError, setStatsError] = useState(false)

  // Totales globales (sin filtros de fecha) para el resumen del sidebar,
  // independientes de los filtros que tenga aplicados la página activa.
  useEffect(() => {
    getDashboardOverview()
      .then((response) => setStats(response.data))
      .catch(() => setStatsError(true))
  }, [])

  const navigation = [
    { 
      name: 'General Overview', 
      href: '/', 
      icon: '📊',
      description: 'Vista general del dashboard'
    },
    { 
      name: 'Flow Performance', 
      href: '/flow-performance', 
      icon: '🌊',
      description: 'Análisis completo de flujos'
    },
    { 
      name: 'Campaign Details', 
      href: '/campaigns-details', 
      icon: '🎯',
      description: 'Detalles de campañas LC'
    },
    { 
      name: 'Revenue Per Day', 
      href: '/revenue-per-day', 
      icon: '💰',
      description: 'Ingresos por día'
    },
    { 
      name: 'Media Overview', 
      href: '/media-overview', 
      icon: '📺',
      description: 'Vista consolidada de medios'
    },
    { 
      name: 'Facebook Details', 
      href: '/facebook-details', 
      icon: '📘',
      description: 'Análisis específico Facebook'
    },
    { 
      name: 'Google Details', 
      href: '/google-details', 
      icon: '🔍',
      description: 'Análisis específico Google'
    },
    { 
      name: 'Microsoft Details', 
      href: '/microsoft-details', 
      icon: '🟦',
      description: 'Análisis específico Microsoft'
    },
    {
      name: 'TikTok Details',
      href: '/tiktok-details',
      icon: '🎵',
      description: 'Análisis específico TikTok'
    },
    {
      name: 'Source Pages',
      href: '/source-pages',
      icon: '🔗',
      description: 'Análisis de páginas de origen'
    },
    {
      name: 'Importar Datos',
      href: '/import-data',
      icon: '📥',
      description: 'Subir nuevos datos vía CSV'
    },
    {
      name: 'Tráfico Sin Asociar',
      href: '/unassociated-traffic',
      icon: '🔀',
      description: 'Reconciliación manual (H5)'
    },
    {
      name: 'Alertas',
      href: '/alerts',
      icon: '🚨',
      description: 'Anomalías y capping (H3)'
    },
    {
      name: 'Predicciones',
      href: '/predictions',
      icon: '🔮',
      description: 'Proyección y recomendaciones (Fase D)'
    },
    // Panel de administrador: solo visible para role === 'admin'. El
    // backend también lo exige (requireAdmin) — esto es solo para no
    // mostrar un enlace que un "operator" no podría usar.
    ...(user?.role === 'admin'
      ? [{
          name: 'Usuarios',
          href: '/users',
          icon: '👥',
          description: 'Gestionar accesos al dashboard'
        }]
      : []),
  ]

  const handleLogout = () => {
    logout()
    navigate('/login', { replace: true })
  }

  const isActive = (href: string) => {
    return location.pathname === href
  }

  return (
    <div className="min-h-screen bg-dashboard-bg">
      {/* Header Corporativo Moderno */}
      <header className="bg-gradient-globo shadow-lg relative overflow-hidden">
        <div className="absolute inset-0 bg-black opacity-10"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center shadow-lg">
                  <span className="text-2xl font-bold text-globo-600">G</span>
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-white">
                    GLOBO DIGITAL
                  </h1>
                  <p className="text-globo-100 text-sm font-medium">
                    Lead Generation Analytics
                  </p>
                </div>
              </div>
            </div>
            
            <div className="flex items-center space-x-6">
              <div className="hidden md:flex items-center space-x-4">
                <div className="text-right">
                  <p className="text-white text-sm font-semibold">Dashboard v2.0</p>
                  <p className="text-globo-100 text-xs">Tiempo real</p>
                </div>
                <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse shadow-glow"></div>
              </div>

              {/* H4 (Fase B #5): sesión iniciada + cerrar sesión */}
              <div className="hidden md:flex items-center space-x-3 border-l border-white/20 pl-6">
                <div className="text-right">
                  <div className="flex items-center justify-end space-x-2">
                    <p className="text-white text-sm font-semibold">{user?.name || user?.email}</p>
                    <span className="text-[10px] uppercase tracking-wide bg-white/20 text-white px-2 py-0.5 rounded-full">
                      {user?.role === 'admin' ? 'Admin' : 'Operador'}
                    </span>
                  </div>
                  <p className="text-globo-100 text-xs">{user?.email}</p>
                </div>
                <button
                  onClick={handleLogout}
                  className="text-white/80 hover:text-white text-sm font-medium transition-colors"
                  title="Cerrar sesión"
                >
                  Cerrar sesión
                </button>
              </div>

              <button
                onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                className="md:hidden text-white hover:text-globo-200 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar Moderno */}
        <nav className={`${sidebarCollapsed ? 'w-20' : 'w-80'} flex-shrink-0 bg-white shadow-xl min-h-screen border-r border-dashboard-border transition-all duration-300 relative`}>
          <div className="p-6">
            {/* Navigation */}
            <div className="space-y-3">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`group flex items-center px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200 ${
                    isActive(item.href)
                      ? 'bg-gradient-globo text-white shadow-metric'
                      : 'text-dashboard-text hover:bg-globo-50 hover:text-globo-700'
                  }`}
                >
                  <span className="text-xl mr-4">{item.icon}</span>
                  {!sidebarCollapsed && (
                    <div className="flex-1">
                      <div className="font-semibold">{item.name}</div>
                      <div className={`text-xs ${isActive(item.href) ? 'text-globo-100' : 'text-dashboard-muted'}`}>
                        {item.description}
                      </div>
                    </div>
                  )}
                  {isActive(item.href) && (
                    <div className="w-2 h-2 bg-white rounded-full"></div>
                  )}
                </Link>
              ))}
            </div>

            {/* Quick Stats */}
            {!sidebarCollapsed && (
              <div className="mt-8 p-4 bg-gradient-card rounded-xl border border-dashboard-border">
                <h3 className="text-sm font-bold text-dashboard-text mb-4 flex items-center">
                  <span className="mr-2">⚡</span>
                  Métricas en Vivo
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-dashboard-muted font-medium">Total Leads</span>
                    <span className="text-sm font-bold text-dashboard-text">
                      {stats ? stats.summary.totalLeads.toLocaleString() : '—'}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-dashboard-muted font-medium">Inversión</span>
                    <span className="text-sm font-bold text-dashboard-text">
                      {stats ? `€${stats.summary.totalSpend.toFixed(2)}` : '—'}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-dashboard-muted font-medium">ROI</span>
                    <span className={`text-sm font-bold ${stats && stats.kpis.roi >= 0 ? 'text-dashboard-success' : 'text-dashboard-error'}`}>
                      {stats ? `${stats.kpis.roi.toFixed(1)}%` : '—'}
                    </span>
                  </div>
                  <div className="pt-2 border-t border-dashboard-border-light">
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-dashboard-muted font-medium">Estado</span>
                      <span className={`badge ${statsError ? 'badge-error' : 'badge-success'}`}>
                        {statsError ? 'Sin conexión' : 'Activo'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </nav>

        {/* Main Content */}
        <main className="flex-1 min-w-0 p-8 bg-dashboard-bg">
          <div className="max-w-7xl mx-auto">
            {children}
          </div>
        </main>
      </div>
    </div>
  )
}

export default Layout
